import type * as React from "react"

import { LEAD_STATUSES, type LeadStatusesValues } from "./lead-statuses.ts"

/**
 * Overlays that can sit on a lead at any stage, several at once. They do not
 * change the lead's stage — mirrors `Project.OperationalState` in ezbuild_back.
 *
 * True overlays only (2026-08-30): parking a lead — Nurturing, Do Not
 * Contact — is a lead *status* now, not an overlay. Inactive has one axis.
 */
const OPERATIONAL_STATES = {
  HOT: "hot",
  FOLLOW_UP: "follow_up",
  ON_HOLD: "on_hold",
  NEEDS_INFO: "needs_info"
} as const

type OperationalStateKeys = keyof typeof OPERATIONAL_STATES
type OperationalState = (typeof OPERATIONAL_STATES)[OperationalStateKeys]

/**
 * The retired parking overlays. A lead record written before the single-axis
 * migration — or echoed back by a stale tab — can still carry these values;
 * the backend strips them on write, and readers here must tolerate them.
 */
const RETIRED_OPERATIONAL_STATES = ["rehash_nurture", "do_not_contact"] as const

/**
 * What a stored row can actually hold: the live overlays plus a retired
 * parking value on a row the crm/0037 migration has not rewritten.
 */
type StoredOperationalState =
  | OperationalState
  | (typeof RETIRED_OPERATIONAL_STATES)[number]

const OPERATIONAL_STATE_LABELS: Record<OperationalState, string> = {
  [OPERATIONAL_STATES.HOT]: "Hot",
  [OPERATIONAL_STATES.FOLLOW_UP]: "Follow-Up",
  [OPERATIONAL_STATES.ON_HOLD]: "On Hold",
  [OPERATIONAL_STATES.NEEDS_INFO]: "Needs Info"
}

/**
 * Taken verbatim from the client's supplied palette, which reuses two of the
 * lifecycle status hues — Hot shares Closed-Lost's red, Follow-Up shares
 * Booked's blue. The two pills are told apart by their own labelled columns
 * rather than by hue, so the reuse is intended and not a collision to fix.
 */
const operationalStateColorMap: Record<OperationalState, React.CSSProperties> =
  {
    [OPERATIONAL_STATES.HOT]: {
      color: "#EF4444",
      backgroundColor: "#FFF0F0",
      borderColor: "#FFB8B8"
    },
    [OPERATIONAL_STATES.FOLLOW_UP]: {
      color: "#3B82F6",
      backgroundColor: "#EAF2FF",
      borderColor: "#B7D2FF"
    },
    [OPERATIONAL_STATES.ON_HOLD]: {
      color: "#F59E0B",
      backgroundColor: "#FFF7E8",
      borderColor: "#FFD79A"
    },
    [OPERATIONAL_STATES.NEEDS_INFO]: {
      color: "#0EA5E9",
      backgroundColor: "#EAF7FF",
      borderColor: "#B7E4FA"
    }
  }

/**
 * Won is not an archived status — it is a converted lead (`is_project=True`).
 * Mirrors `WON_REASON_KEY` in ezbuild_back.
 */
const WON_REASON_KEY = "won" as const

/**
 * The Inactive reason keys are a wire contract; they keep their overlay-era
 * spellings (`rehash_nurture` for the Nurturing reason) so an installed
 * client and a freshly deployed backend agree through the deploy window —
 * mirrors `NURTURE_REASON_KEY` / `DO_NOT_CONTACT_REASON_KEY` in ezbuild_back.
 */
const NURTURE_REASON_KEY = "rehash_nurture" as const
const DO_NOT_CONTACT_REASON_KEY = "do_not_contact" as const

type InactiveReasonKey =
  | typeof NURTURE_REASON_KEY
  | typeof DO_NOT_CONTACT_REASON_KEY
  | "lost"
  | "unqualified"
  | typeof WON_REASON_KEY

/**
 * One entry per inactive reason, in the order the Inactive view lists them —
 * mirrors `INACTIVE_REASONS` in ezbuild_back. Every non-Won reason is an
 * archived status now.
 */
const INACTIVE_REASONS: ReadonlyArray<{
  state: InactiveReasonKey
  label: string
}> = [
  { state: NURTURE_REASON_KEY, label: "Nurturing" },
  { state: "lost", label: "Lost" },
  { state: "unqualified", label: "Unqualified" },
  { state: DO_NOT_CONTACT_REASON_KEY, label: "Do Not Contact" },
  { state: WON_REASON_KEY, label: "Converted to Project" }
]

/** Whether a stored value is one of the four overlays the UI can render. */
const isLiveOverlay = (state: string): state is OperationalState =>
  state in OPERATIONAL_STATE_LABELS

/**
 * Whether a lead is parked for a given reason, tolerating a row written
 * before the backend's crm/0037 migration ran.
 *
 * The five repos deploy independently, so until that migration lands a lead
 * can still say "do not contact" through the retired overlay while its status
 * reads NEW. Every consent-bearing read has to accept both spellings or the
 * deploy window quietly re-offers a customer the contact they refused.
 */
const carriesRetiredState = (
  states: readonly string[] | null | undefined,
  retired: (typeof RETIRED_OPERATIONAL_STATES)[number]
) => (states ?? []).includes(retired)

/**
 * The parking reasons a row still carries through a retired overlay. The
 * retired values are spelled as the reason keys, so this is the reason set
 * for a row the migration has not rewritten.
 */
const retiredParkingReasons = (
  states?: readonly string[] | null
): InactiveReasonKey[] =>
  RETIRED_OPERATIONAL_STATES.filter((retired) =>
    carriesRetiredState(states, retired)
  )

/**
 * The archived status each parked reason names — the single pairing every
 * reader shares, so a chip, a row menu and a filter cannot drift apart. Won
 * is a conversion (`is_project`), not an archived status, so it is absent.
 */
const REASON_STATUSES: Record<
  Exclude<InactiveReasonKey, typeof WON_REASON_KEY>,
  LeadStatusesValues
> = {
  [NURTURE_REASON_KEY]: LEAD_STATUSES.NURTURING,
  lost: LEAD_STATUSES.LOST,
  unqualified: LEAD_STATUSES.UNQUALIFIED,
  [DO_NOT_CONTACT_REASON_KEY]: LEAD_STATUSES.DO_NOT_CONTACT
}

/**
 * A lead is reactivate-eligible when it sits on an archived status (Lost /
 * Unqualified / Nurturing / Do Not Contact, or closed-lost) — mirrors the
 * backend's `reactivate` action.
 *
 * A row the crm/0037 migration has not rewritten is still parked by a retired
 * overlay while `is_archived` reads false, and keying on the flag alone would
 * leave it no way back from the UI for the whole deploy window.
 */
const canReactivate = (
  isArchived?: boolean,
  states?: readonly string[] | null
) => !!isArchived || retiredParkingReasons(states).length > 0

/** Drop the retired parking values from anything the user can pick or filter by. */
const withoutRetiredStates = <T extends string>(states?: readonly T[] | null) =>
  (states ?? []).filter(
    (state) =>
      !(RETIRED_OPERATIONAL_STATES as readonly string[]).includes(state)
  )

export {
  carriesRetiredState,
  isLiveOverlay,
  retiredParkingReasons,
  withoutRetiredStates,
  DO_NOT_CONTACT_REASON_KEY,
  INACTIVE_REASONS,
  REASON_STATUSES,
  NURTURE_REASON_KEY,
  OPERATIONAL_STATES,
  OPERATIONAL_STATE_LABELS,
  RETIRED_OPERATIONAL_STATES,
  WON_REASON_KEY,
  canReactivate,
  operationalStateColorMap,
  type InactiveReasonKey,
  type OperationalState,
  type StoredOperationalState
}
